<script setup>
import { computed, ref } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import MultiSelectFilter from './MultiSelectFilter.vue';
import CustomAttributeFilter from './CustomAttributeFilter.vue';
import SelectedFiltersDisplay from './SelectedFiltersDisplay.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  filterListOptions: {
    type: Array,
    required: true,
  },
  filterData: {
    type: Object,
    required: true,
  },
  visibleFilters: {
    type: Array,
    required: true,
  },
  ticketAttributes: {
    type: Array,
    default: () => [],
  },
  searchQuery: {
    type: String,
    default: '',
  },
  selectedFilters: {
    type: Array,
    default: () => [],
  },
  getSelectedItems: {
    type: Function,
    required: true,
  },
  handleItemSelect: {
    type: Function,
    required: true,
  },
  handleCustomAttributeFilter: {
    type: Function,
    required: true,
  },
  getCustomAttributeFilterValue: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  handleClearFilters: {
    type: Function,
    required: true,
  },
  handleSearch: {
    type: Function,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    default: 'Pesquisar...',
  },
  filtersContainerClass: {
    type: String,
    default: 'px-0 py-4 border-b border-n-weak mb-4',
  },
  selectedFiltersContainerClass: {
    type: String,
    default: 'px-0 py-4 border-b border-n-weak mb-4',
  },
});

const emit = defineEmits(['update:searchQuery']);
const searchInputRef = ref(null);

const localSearchQuery = computed({
  get: () => props.searchQuery,
  set: value => emit('update:searchQuery', value),
});

const visibleBaseFilters = computed(() =>
  props.filterListOptions.filter(option => option.key !== 'custom_attributes')
);
const displayedFilters = computed(() =>
  visibleBaseFilters.value.filter(option =>
    props.visibleFilters.includes(option.key)
  )
);

const getInputPlaceholder = option =>
  `Buscar ${String(option.label || '').toLowerCase()}...`;

defineExpose({
  searchInputRef,
});
</script>

<template>
  <div>
    <div :class="filtersContainerClass">
      <div class="flex flex-col lg:flex-row gap-4">
        <div class="flex-1 max-w-md py-2">
          <Input
            ref="searchInputRef"
            v-model="localSearchQuery"
            :placeholder="searchPlaceholder"
            :disabled="loading"
            :custom-input-class="[
              'h-10 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-10 !py-2 rtl:!pr-10',
            ]"
            @input="handleSearch"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-3 rtl:right-3"
              />
            </template>
          </Input>
        </div>

        <div class="flex-1 min-w-0 overflow-x-auto hidden md:block">
          <div class="flex gap-2 p-2">
            <MultiSelectFilter
              v-for="option in displayedFilters"
              :key="option.key"
              :options="filterData[option.key] || []"
              :selected-items="
                getSelectedItems(option.key, filterData[option.key] || [])
              "
              multiselector-title="Filtrar"
              :multiselector-placeholder="option.label"
              :input-placeholder="getInputPlaceholder(option)"
              :icon="option.icon"
              :disabled="loading"
              @select="item => handleItemSelect(option.key, item)"
            />

            <template
              v-if="
                ticketAttributes.length > 0 &&
                visibleFilters.includes('custom_attributes')
              "
            >
              <CustomAttributeFilter
                v-for="attribute in ticketAttributes"
                :key="attribute.id"
                :attribute="attribute"
                :selected-value="
                  getCustomAttributeFilterValue(attribute.attributeKey)
                "
                :disabled="loading"
                @apply-filter="handleCustomAttributeFilter"
                @clear-filter="handleCustomAttributeFilter"
              />
            </template>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selectedFilters.length > 0"
      :class="selectedFiltersContainerClass"
    >
      <SelectedFiltersDisplay
        :selected-filters="selectedFilters"
        show-clear-all
        @remove-filter="removeFilter"
        @clear-all="handleClearFilters"
      />
    </div>
  </div>
</template>
